import moment from "moment-timezone";
import { OtcContractLayerDto } from "@/lib/api/otc";
import { formatDateWithoutTimezone } from "@/lib/api/date";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { getOtcLayerFieldDef } from "./fieldRegistry";
import { OtcLayerFormValues, layerToFormValues } from "./snapshotToForm";

/**
 * Serializes a Date into the API string convention used across the OTC forms
 * (timezone-aware ISO at the Common destination timezone). Mirrors the private
 * `dateToApiString` helper in the v2 transform so layer patches match what the
 * backend already expects from contract create/update.
 */
function dateToApiString(value: Date): string {
  return moment
    .tz(formatDateWithoutTimezone(value), "YYYY/MM/DD", CommonDestinationTimezone)
    .toISOString();
}

/**
 * Normalizes a single form value into the scalar shape the API compares/stores,
 * keyed off the field's registry widget. Returns `undefined` for empty values
 * so "empty" comparisons and explicit clears are handled uniformly.
 */
function normalizeForApi(field_name: string, value: unknown): unknown {
  const def = getOtcLayerFieldDef(field_name);
  if (!def) return undefined;

  if (value === null || value === undefined) return undefined;

  switch (def.widget) {
    case "date": {
      if (value instanceof Date) return dateToApiString(value);
      if (typeof value === "string" && value !== "") return value;
      return undefined;
    }
    case "checkbox":
      return typeof value === "boolean" ? value : undefined;
    case "int": {
      if (typeof value === "number") return value;
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? undefined : parsed;
      }
      return undefined;
    }
    // text / float / select all carry strings in form state
    default: {
      if (typeof value === "string") return value === "" ? undefined : value;
      if (typeof value === "number") return String(value);
      return undefined;
    }
  }
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null;
}

interface BuildCurrentLayerUpdatePatchParams {
  layer: OtcContractLayerDto;
  formValues: OtcLayerFormValues;
  /**
   * Decides whether a field is currently relevant to the contract state. When
   * provided, a hidden/irrelevant field may only be CLEARED (sent as `null`
   * when it previously had a value); it is never sent with a new/changed value.
   * This prevents an irrelevant field (e.g. a Canola futures field on a Corn
   * contract) from being pushed to the backend. Omit to disable the check.
   */
  isFieldVisible?: (field_name: string) => boolean;
}

/**
 * Builds the minimal `data` patch for `PUT /otc/contracts/:id/layers`.
 *
 * - Baseline is the current layer adapted into form values, so unchanged fields
 *   compare equal after the same normalization.
 * - Only fields the backend says the layer owns (`owned_fields`) are eligible.
 * - Only fields present in the v3 registry are sent. This intentionally
 *   excludes backend-derived fields (margin, adj_margin*, expected/futures
 *   margins, final_futures_price_margin), which are not in the registry and are
 *   always recomputed by the backend, even if `owned_fields` lists them for
 *   legacy compatibility.
 * - Only fields that actually changed are included.
 * - Explicit clears are preserved by sending the key as `null`.
 */
export function buildCurrentLayerUpdatePatch({
  layer,
  formValues,
  isFieldVisible,
}: BuildCurrentLayerUpdatePatchParams): Record<string, unknown> {
  const baselineValues = layerToFormValues(layer);
  const patch: Record<string, unknown> = {};

  for (const field of layer.owned_fields) {
    if (!getOtcLayerFieldDef(field)) continue;

    const baselineVal = normalizeForApi(field, baselineValues[field]);
    const currentVal = normalizeForApi(field, formValues[field]);

    const baselineEmpty = isEmpty(baselineVal);
    const currentEmpty = isEmpty(currentVal);

    if (baselineEmpty && currentEmpty) continue;

    if (currentEmpty && !baselineEmpty) {
      // User cleared an owned field that previously had a value. Always send the
      // clear, even for a now-hidden field, so stale irrelevant values are
      // removed (e.g. Canola fields after switching commodity to Corn).
      patch[field] = null;
      continue;
    }

    // NOTE: Some fields are hidden but kept around.
    // And have not been removed from the api yet, so are still required at the api level
    const isDeprecatedHeldField = ["has_invoice_fees", "has_contract_adjustment"].includes(field);

    // From here the field has a non-empty current value. Never push a value for
    // a field that is not relevant to the current contract state.
    if (!isDeprecatedHeldField && isFieldVisible && !isFieldVisible(field)) continue;

    if (baselineEmpty && !currentEmpty) {
      patch[field] = currentVal;
      continue;
    }

    if (currentVal !== baselineVal) {
      patch[field] = currentVal;
    }
  }

  return patch;
}
